import React, { useEffect } from 'react'
import { ExternAppointmentSchedule } from 'modules/calendar/models/ExternAppointmentSchedule'
import styles from './NewScheduleModal.module.css'
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Slider,
} from '@mui/material'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Box, fontFamily, styled } from '@mui/system'
import { AppButton, ButtonTheme } from 'components/app-button/AppButton'
import { AppCheckbox } from 'components/app-checkbox/AppCheckbox'
import { WeekDay } from 'modules/calendar/enums/WeekDay'

interface NewScheduleModalProps {
  schedule?: ExternAppointmentSchedule
  open: boolean
  handleSave: (selectedDays: WeekDay[], value: number[]) => void
  handleClose: () => void
  handleCheckSchedules: (selectedDays: WeekDay[], value: number[]) => void
  error: string
}

const CareMeSlider = styled(Slider)(({ theme }) => ({
  color: '#2d7161',
  height: 8,
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    border: '2px solid currentColor',
    '&:hover': {
      boxShadow: '0 0 0 8px rgba(0,0,0,0.16)',
    },
    '&:active': {
      boxShadow: '0 0 0 8px rgba(45, 113, 97, 0.3)',
    },
  },
  '& .MuiSlider-valueLabel': {
    fontFamily: "'Montserrat-Regular', sans-serif",
    top: 50,
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    '& *': {
      background: 'transparent',
      color: 'inherit',
    },
  },
}))

export const NewScheduleModal: React.FC<NewScheduleModalProps> = ({
  open,
  handleSave,
  handleClose,
  handleCheckSchedules,
  error,
  schedule,
}) => {
  const [value, setValue] = React.useState<number[]>([9, 12])
  const { t } = useTranslation()
  const weekDays: { name: string; enumValue: WeekDay }[] = [
    { name: 'monday', enumValue: WeekDay.Monday },
    { name: 'tuesday', enumValue: WeekDay.Tuesday },
    { name: 'wednesday', enumValue: WeekDay.Wednesday },
    { name: 'thursday', enumValue: WeekDay.Thursday },
    { name: 'friday', enumValue: WeekDay.Friday },
    { name: 'saturday', enumValue: WeekDay.Saturday },
    { name: 'sunday', enumValue: WeekDay.Sunday },
  ]
  const [selectedDays, setSelectedDays] = React.useState<WeekDay[]>([])

  useEffect(() => {
    if (!schedule) {
      setSelectedDays([])
      setValue([9, 12])
      return
    }

    setSelectedDays(schedule.weekDays)
    const scheduleFrom = Number(schedule.from.split(':')[0])
    const scheduleTo = Number(schedule.to.split(':')[0])
    setValue([scheduleFrom, scheduleTo])
  }, [schedule])

  const handleCheckboxChange = (day: WeekDay) => {
    setSelectedDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]))
  }

  const handleChange = (_event: Event, newValue: number | number[], activeThumb: number) => {
    const [min, max] = newValue as number[]

    if (max - min < 1) {
      if (activeThumb === 0) {
        setValue([max - 1, max])
      } else {
        setValue([min, min + 1])
      }
    } else {
      setValue([min, max])
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: styles.dialog }}
    >
      <DialogTitle>{schedule ? t('updateSchedule') : t('addSchedule')}</DialogTitle>

      <DialogContent>
        <Box className={styles.dialogContentContainer}>
          <CareMeSlider
            getAriaLabel={() => 'schedule'}
            onChange={handleChange}
            value={value}
            step={1}
            min={7}
            max={22}
            valueLabelDisplay="on"
            valueLabelFormat={(val) => `${val}:00`}
            disableSwap
            onChangeCommitted={() => handleCheckSchedules(selectedDays, value)}
          />

          <FormGroup className={styles.daysContainer}>
            {weekDays.map(({ name, enumValue }) => (
              <FormControlLabel
                key={name}
                control={
                  <Checkbox
                    className={styles.checkbox}
                    checked={selectedDays.includes(enumValue)}
                    onChange={() => handleCheckboxChange(enumValue)}
                  />
                }
                label={t(name)}
              />
            ))}
          </FormGroup>

          {error && <Box className={styles.errorContainer}>{error}</Box>}
        </Box>
      </DialogContent>
      <DialogActions className={styles.actionsContainer}>
        <AppButton
          type="button"
          handler={handleClose}
          theme={ButtonTheme.NewSecondary}
          label={t('cancel')}
        />
        <AppButton
          type="button"
          handler={() => handleSave(selectedDays, value)}
          theme={ButtonTheme.NewPrimary}
          label={t('save')}
        />
      </DialogActions>
    </Dialog>
  )
}
